/**
 * Script for the Front Page
 *
 * @author Space-Time Inc.
 * @version 2023-02-28
 */

/**
 * Script for Splash Page
 *
 * @author Space-Time Inc.
 */

(() => {
	const KEY   = 'splash';
	const LIMIT = (1000 * 60 * 30);  // 30 min.

	initSplash();
	initResetLink();

	function initSplash() {
		if (!location.hash && LIMIT < getElapsedTime()) {
			window.scrollTo(0, 0);
			setTimeout(() => {
				document.body.classList.add('splash');
			}, 0);
			setCurrentTime();
		} else {
			document.body.classList.add('no-splash');
		}
	}

	function initResetLink() {
		const show = document.querySelector('#show-splash');
		show.addEventListener('click', () => {
			reset();
			const a = document.querySelector('.site-header .site-branding a');
			a.click();
		});
	}

	function getElapsedTime() {
		const time = localStorage.getItem(KEY);
		if (parseInt(time, 10) === -1) {
			setCurrentTime();
			return 0;
		}
		return time ? Date.now() - time : Number.MAX_VALUE;
	}

	function setCurrentTime() {
		localStorage.setItem(KEY, Date.now());
	}

	function reset() {
		localStorage.removeItem(KEY);
	}
})();

window.addEventListener('DOMContentLoaded', () => {
	const base = document.querySelector('.part-splash-page');
	if (!base) return;

	const filmCount = base.querySelectorAll('.filmstrip img').length;
	base.style.setProperty('--filmstrip-count', filmCount);

	const skip = base.querySelector('#skip-splash');
	skip.addEventListener('click', () => {
		base.style.display = 'none';
		document.body.classList.remove('splash');
		document.body.classList.add('skip-splash');
	});

	const goNext = initializeSceneAnimation({
		root                : document.body,
		datasetFrameCount   : 'frame',
		datasetStepNextFrame: 'snf',
		delay               : 400,
		onFinishing         : () => document.body.classList.add('sa-finishing'),
	});

	checkImageLoading({
		root           : base.querySelector('.filmstrip'),
		propertyLoading: '--sa-image-loading',
		onLoaded       : () => {
			document.body.classList.add('sa-image-loaded');
			setTimeout(goNext, 1000);
		}
	});

	/**
	 * Throttling Function
	 *
	 * @author Space-Time Inc.
	 * @version 2022-10-13
	 */
	
	function throttle(fn) {
		let isRunning;
		return (...args) => {
			if (isRunning) return;
			isRunning = true;
			requestAnimationFrame(() => {
				isRunning = false;
				fn(...args);
			});
		};
	}
	
	/**
	 * Function for Checking Image Loading
	 *
	 * @author Space-Time Inc.
	 * @version 2023-04-04
	 */
	
	function checkImageLoading(opts = {}) {
		opts = Object.assign({
			root           : document.body,
			propertyLoading: '--image-loading',
			onLoaded       : null,
		}, opts);
	
		const imgs   = opts.root.querySelectorAll('img');
		const loaded = new Set();
	
		let st   = null;
		let done = false;
	
		for (const img of imgs) {
			img.loading = 'eager';
	
			if (img.complete) {
				doLoaded(img);
			} else {
				img.addEventListener('load', () => doLoaded(img));
			}
		}
	
		const fn = () => {
			if (!check()) {
				st = setTimeout(() => fn(), 100);
			}
		};
		fn();
	
		function check() {
			for (const img of imgs) {
				if (!loaded.has(img) && img.complete) {
					doLoaded(img);
				}
			}
			return imgs.length === loaded.size;
		}
	
		function doLoaded(img) {
			loaded.add(img);
			document.body.style.setProperty(opts.propertyLoading, `${loaded.size / imgs.length * 100}%`);
	
			if (!done && imgs.length === loaded.size) {
				clearTimeout(st);
				done = true;
				if ('function' === typeof opts.onLoaded) {
					setTimeout(() => opts.onLoaded(), 200);
				}
			}
		}
	}
	
	/**
	 * Function for Scene Animation
	 *
	 * @author Space-Time Inc.
	 * @version 2023-02-06
	 */
	
	function initializeSceneAnimation(opts = {}) {
		opts = Object.assign({
			root                : document.body,
			datasetFrameCount   : 'frame',
			datasetStepNextFrame: 'snf',
			delay               : 0,
			onFinishing         : null,
			onFinished          : null,
		}, opts);
	
		const r   = opts.root;
		const key = opts.datasetFrameCount;
		const ts  = Array.from(r.querySelectorAll(`*[data-${opts.datasetStepNextFrame}]`));
		const max = ts.length;
	
		for (const elm of ts) {
			elm.addEventListener('transitionend', e => {
				e.preventDefault();
				if (elm === e.target) {
					setTimeout(step, opts.delay);
				}
			});
		}
	
		r.dataset[key] = '';
	
		function step() {
			if (r.dataset[key].length === max) {
				if ('function' === typeof opts.onFinished) {
					opts.onFinished();
				}
				return;
			}
			r.dataset[key] += '.';
	
			if (r.dataset[key].length === max) {
				if ('function' === typeof opts.onFinishing) {
					opts.onFinishing();
				}
			}
		}
	
		return step;
	}
	
});

/**
 * Dynamic Filter - Lists by Terms
 *
 * @author Space-Time Inc.
 * @version 2024-02-28
 */

(() => {
	class DynamicFilter {
		#maxCount;

		#bs;
		#its;
		#as;

		constructor(flt, list, arch) {
			this.#maxCount = parseInt(list.dataset?.maxCount ?? '6');

			this.#bs  = flt.querySelectorAll('button');
			this.#its = list.querySelectorAll(':scope > li');
			this.#as  = arch.querySelectorAll('a');

			this.#hideEmpty(list);
			for (const b of this.#bs) b.addEventListener('click', e => this.#onClick(e));
			this.#bs[0].click();
		}

		#hideEmpty(list) {
			for (const b of this.#bs) {
				const t = b.dataset.type;
				if ('all' === t) continue;

				const it = list.querySelector(`:scope > li.${t}`);
				if (!it) {
					b.classList.add('empty');
					const p = b.parentElement;
					if ('LI' === p.tagName) p.classList.add('empty');
				}
			}
		}

		#onClick(e) {
			const cur = e.currentTarget;
			for (const b of this.#bs) {
				const p = b.parentElement;
				if (cur === b) {
					b.classList.add('current');
					if ('LI' === p.tagName) p.classList.add('current');
				} else {
					b.classList.remove('current');
					if ('LI' === p.tagName) p.classList.remove('current');
				}
			}
			const t = cur.dataset.type;
			for (const a of this.#as) {
				a.style.display = (t === a.dataset.type) ? '' : 'none';
			}
			this.#filterItems(t, this.#maxCount);
		}

		#filterItems(t, count) {
			for (const it of this.#its) {
				if (count) {
					const v = ('all' === t) || (it.classList.contains(t));
					if (v) {
						it.style.display = '';
						count -= 1;
					} else {
						it.style.display = 'none';
					}
				} else {
					it.style.display = 'none';
				}
			}
		}
	}

	const fs = document.querySelectorAll('.part-dynamic-filter');
	if (1 === fs.length) {
		let l = document.querySelector('.part-dynamic-filter + .list-item');
		let a = document.querySelector('.part-dynamic-archive');

		if (!l) {
			const id = fs[0].dataset?.filterId ?? '';
			if (id) {
				l = document.querySelector(`.list-item[data-filter-id='${id}']`);
				if (!a) a = document.querySelector(`.part-dynamic-archive[data-filter-id='${id}']`);
			}
		}
		if (l && a) new DynamicFilter(fs[0], l, a);
	} else {
		for (const f of fs) {
			const id = f.dataset?.filterId ?? '';
			if (id) {
				const l = document.querySelector(`.list-item[data-filter-id='${id}']`);
				const a = document.querySelector(`.part-dynamic-archive[data-filter-id='${id}']`);
				if (l && a) new DynamicFilter(f, l, a);
			}
		}
	}
})();

